import { memo } from 'react';
import { useSelector } from '@xstate/store/react';
import { ALL_CONNECTOR_SIDES } from '../../types';
import { NODE_WIDTH, NODE_HEIGHT, getTaskData } from '../../entityTypes';
import type { CanvasEntity } from '../../entityTypes';
import { useTaskCategories } from '../../../stores/hooks';
import { useEmployees } from '../../../stores';
import type { TaskCategory } from '../../../types';
import { globalTasksStore } from '../../../stores/globalTasksStore';
import { useMergedDropTarget } from '../../hooks/useMergedDropTarget';
import { canvasSelectionStore } from '../../stores/canvasSelectionStore';
import { useEntityData } from '../../stores/EntityDataContext';
import { CanvasConnector } from '../../components/CanvasConnector';

export interface CanvasNodeProps {
  entity: CanvasEntity;
}

function CanvasNodeInner({ entity }: CanvasNodeProps) {
  const globalTasksById = useSelector(globalTasksStore, s => s.context.tasksById);
  const { mergedDropTargetId, mergedDropTargetSide } = useMergedDropTarget();
  const { selectedEdgeConnectors, connectorHasEdge } = useEntityData();

  const task = globalTasksById[entity.id];
  const d = getTaskData(entity);

  const isSelected = useSelector(canvasSelectionStore, s => s.context.selectedEntityIds.includes(entity.id));
  const isDropTarget = mergedDropTargetId === entity.id;
  const dropTargetSide = isDropTarget ? mergedDropTargetSide : null;

  const categories = useTaskCategories();
  const employees = useEmployees();

  const categoryDots = (task?.categoryIds ?? [])
    .map(id => categories.find((c: TaskCategory) => c.id === id))
    .filter(Boolean) as TaskCategory[];

  const primaryCategory = categoryDots[0];
  const barColor = primaryCategory?.color ?? '#d1d5db';

  const est = task?.defaultEstimate;
  const estimateLabel = est ? `${est.m}d` : null;

  const assigneeNames = (task?.assigneeIds ?? [])
    .map(id => employees.find(e => e.id === id))
    .filter(Boolean)
    .map(e => e!.name.split(' ')[0]);

  if (!task) return null;

  return (
    <div
      data-canvas-entity
      data-entity-id={entity.id}
      data-entity-kind={entity.kind}
      data-canvas-node={entity.id}
      style={{
        position: 'absolute',
        left: entity.x,
        top: entity.y,
        width: NODE_WIDTH,
        height: NODE_HEIGHT,
      }}
      className={`select-none ${isSelected ? 'ring-2 ring-blue-400' : ''} ${isDropTarget ? 'ring-2 ring-green-400' : ''}`}
    >
      <div
        className="absolute left-0 top-0 bottom-0 w-[3px] rounded-l"
        style={{ backgroundColor: barColor }}
      />

      <div
        className="rounded shadow-sm h-full pl-2 pr-2 py-1 flex flex-col justify-center overflow-hidden cursor-grab active:cursor-grabbing"
        style={{
          backgroundColor: d.backgroundColor || '#ffffff',
          border: d.borderColor
            ? `${d.borderWidth ?? 1}px solid ${d.borderColor}`
            : '1px solid #e5e7eb',
        }}
      >
        <div className="flex items-center gap-1.5 min-w-0">
          {categoryDots.length > 0 && (
            <div className="flex gap-0.5 flex-shrink-0">
              {categoryDots.map(cat => (
                <div
                  key={cat.id}
                  className="w-2 h-2 rounded-full flex-shrink-0"
                  style={{ backgroundColor: cat.color }}
                  title={cat.name}
                />
              ))}
            </div>
          )}
          <span
            className="truncate flex-1"
            style={{
              color: d.textColor || '#1f2937',
              fontSize: d.fontSize || 12,
              fontWeight: d.fontWeight || 'normal',
            }}
          >{task.title}</span>
          {estimateLabel && (
            <span className="text-[10px] text-gray-400 flex-shrink-0 bg-gray-50 px-1 rounded">
              {estimateLabel}
            </span>
          )}
        </div>

        {assigneeNames.length > 0 && (
          <div className="flex items-center gap-1 mt-0.5 min-w-0">
            <span className="text-[9px] truncate" style={{ color: d.textColor || '#9ca3af' }}>
              {assigneeNames.join(', ')}
            </span>
          </div>
        )}
      </div>

      {entity.connectable !== false && ALL_CONNECTOR_SIDES.map(side => {
        const key = `${entity.id}:${side}`;
        return (
          <CanvasConnector
            key={side}
            taskId={entity.id}
            side={side}
            isDropTarget={isDropTarget}
            dropTargetSide={dropTargetSide}
            isEdgeEndpoint={selectedEdgeConnectors.has(key)}
            hasEdge={connectorHasEdge.has(key)}
          />
        );
      })}
    </div>
  );
}

export const CanvasNode = memo(CanvasNodeInner);
